import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

// Copyright 1999-2020. Plesk International GmbH. All rights reserved.

/* eslint-disable react/no-deprecated */
import React, { Component } from 'react';
import { safeInvoke } from '../utils';
import { isRtl } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';

/**
 * `CodeEditor` component is used for editing text as code with proper color highlighting according to selected programming language.
 * @since 0.0.61
 */
var CodeEditor =
/*#__PURE__*/
function (_Component) {
  _inherits(CodeEditor, _Component);

  function CodeEditor() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, CodeEditor);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(CodeEditor)).call.apply(_getPrototypeOf2, [this].concat(args)));

    _defineProperty(_assertThisInitialized(_this), "state", {
      content: _this.props.children,
      lineSeparator: _this.getLineSeparator(_this.props.children),
      options: {
        lineNumbers: true,
        lineWrapping: true,
        matchBrackets: true,
        matchTags: {
          bothTags: true
        },
        indentUnit: 4,
        indentWithTabs: true,
        enterMode: 'keep',
        tabMode: 'shift',
        autoRefresh: true,
        extraKeys: {
          'Ctrl-S': 'save'
        },
        direction: isRtl() ? 'rtl' : 'ltr'
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleScroll", function () {
      var onScroll = _this.props.onScroll;
      safeInvoke(onScroll, _this.codeMirror.getScrollInfo());
    });

    _defineProperty(_assertThisInitialized(_this), "handleChange", function (doc, changes) {
      if (_this.props.readOnly || changes.origin === 'setValue') {
        return;
      }

      var lineSeparator = _this.state.lineSeparator;
      var onChange = _this.props.onChange;
      var content = doc.getValue(lineSeparator);

      _this.setState({
        content: content
      }, function () {
        safeInvoke(onChange, content);
      });
    });

    return _this;
  }

  _createClass(CodeEditor, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      var _this2 = this;

      import(
      /* webpackChunkName: "codemirror" */
      './CodeMirror').then(function (_ref) {
        var CodeMirror = _ref.CodeMirror;
        _this2.CodeMirror = CodeMirror;
        var _this2$props = _this2.props,
            onInit = _this2$props.onInit,
            scrollPosition = _this2$props.scrollPosition;
        var options = _this2.state.options;
        _this2.codeMirror = CodeMirror.fromTextArea(_this2.textareaNode, options);

        _this2.codeMirror.on('scroll', _this2.handleScroll);

        if (scrollPosition) {
          _this2.codeMirror.scrollTo(scrollPosition.left, scrollPosition.top);
        }

        _this2.updateMode(_this2.props);

        CodeMirror.commands.save = _this2.props.onSave;
        CodeMirror.commands.find = CodeMirror.commands.findPersistent;
        CodeMirror.commands.findNext = CodeMirror.commands.findPersistentNext;
        CodeMirror.commands.findPrev = CodeMirror.commands.findPersistentPrev;
        safeInvoke(onInit, _this2.codeMirror.display.wrapper);

        _this2.codeMirror.on('change', _this2.handleChange);
      });
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      var _this3 = this;

      if (!this.codeMirror) {
        return;
      }

      Object.keys(this.state.options).forEach(function (name) {
        _this3.codeMirror.setOption(name, _this3.state.options[name]);
      });
      var _this$props = this.props,
          children = _this$props.children,
          mode = _this$props.mode,
          fileName = _this$props.fileName,
          autofocus = _this$props.autofocus,
          scrollPosition = _this$props.scrollPosition;
      this.updateMode({
        mode: mode,
        fileName: fileName
      });
      var newContent = children || '';

      if (newContent !== prevProps.children && newContent !== this.codeMirror.getValue(this.state.lineSeparator)) {
        var prevScrollPosition = prevProps.scrollPosition || this.codeMirror.getScrollInfo();
        this.codeMirror.setValue(newContent);
        this.codeMirror.scrollTo(prevScrollPosition.left, prevScrollPosition.top);

        if (autofocus) {
          this.codeMirror.focus();
        }
      } else if (scrollPosition !== prevProps.scrollPosition) {
        this.codeMirror.scrollTo(scrollPosition.left, scrollPosition.top);
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      if (this.codeMirror) {
        this.codeMirror.off('scroll', this.handleScroll);
        this.codeMirror.toTextArea();
      }
    }
  }, {
    key: "findModeByFileName",
    value: function findModeByFileName(fileName) {
      var modeInfo = this.CodeMirror.findModeByFileName(fileName);

      if (modeInfo) {
        return modeInfo.mode;
      }

      return null;
    }
  }, {
    key: "updateMode",
    value: function updateMode(_ref2) {
      var mode = _ref2.mode,
          fileName = _ref2.fileName;

      if (!mode && fileName) {
        mode = this.findModeByFileName(fileName);
      }

      if (!mode) {
        return;
      }

      this.CodeMirror.loadMode(this.codeMirror, mode);
    }
  }, {
    key: "getLineSeparator",
    value: function getLineSeparator() {
      var content = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
      var sep = /^.*?(\r\n?|\n)/.exec(content);
      return sep && sep[1] ? sep[1] : '\n';
    }
  }, {
    key: "render",
    value: function render() {
      var _this4 = this;

      var _this$props2 = this.props,
          baseClassName = _this$props2.baseClassName,
          className = _this$props2.className,
          mode = _this$props2.mode,
          fileName = _this$props2.fileName,
          readOnly = _this$props2.readOnly,
          autofocus = _this$props2.autofocus,
          children = _this$props2.children,
          onInit = _this$props2.onInit,
          onChange = _this$props2.onChange,
          onSave = _this$props2.onSave,
          options = _this$props2.options,
          scrollPosition = _this$props2.scrollPosition,
          onScroll = _this$props2.onScroll,
          props = _objectWithoutProperties(_this$props2, ["baseClassName", "className", "mode", "fileName", "readOnly", "autofocus", "children", "onInit", "onChange", "onSave", "options", "scrollPosition", "onScroll"]);

      var content = this.state.content;
      return React.createElement("textarea", _extends({
        style: {
          display: 'none'
        },
        ref: function ref(_ref3) {
          return _this4.textareaNode = _ref3;
        },
        className: classNames(baseClassName, className),
        defaultValue: content
      }, props));
    }
  }], [{
    key: "getDerivedStateFromProps",
    value: function getDerivedStateFromProps(_ref4, state) {
      var readOnly = _ref4.readOnly,
          autofocus = _ref4.autofocus,
          options = _ref4.options;
      return {
        options: _objectSpread({}, state.options, {}, options, {
          autofocus: autofocus,
          readOnly: readOnly
        })
      };
    }
  }]);

  return CodeEditor;
}(Component);

CodeEditor.propTypes = {
  /**
   * The mode to use. When not given, this will default to the first mode that was loaded.
   * @since 0.0.61
   */
  mode: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

  /**
   * CodeEditor can detect mode by file name. Will be ignored if you set mode property.
   * @since 0.0.61
   */
  fileName: PropTypes.string,

  /**
   * Content of the `CodeEditor`.
   * @since 0.0.61
   */
  children: PropTypes.string,

  /**
   * Set read only.
   * @since 0.0.61
   */
  readOnly: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),

  /**
   * Set auto focus.
   * @since 0.0.61
   */
  autofocus: PropTypes.bool,

  /**
   * onInit event handler.
   * @since 0.0.61
   */
  onInit: PropTypes.func,

  /**
   * onChange event handler.
   * @since 0.0.61
   */
  onChange: PropTypes.func,

  /**
   * onSave event handler.
   * @since 0.0.61
   */
  onSave: PropTypes.func,

  /**
   * See all supported options in [CodeMirror](https://codemirror.net/doc/manual.html#config) documentation.
   * @since 0.0.61
   */
  options: PropTypes.object,

  /**
   * Set scroll position.
   * @since 1.12.4
   */
  scrollPosition: PropTypes.shape({
    left: PropTypes.number,
    top: PropTypes.number
  }),

  /**
   * onScroll event handler.
   * @since 1.12.4
   */
  onScroll: PropTypes.func,

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * @ignore
   */
  baseClassName: PropTypes.string
};
CodeEditor.defaultProps = {
  mode: '',
  fileName: '',
  children: '',
  readOnly: false,
  autofocus: false,
  onInit: null,
  onChange: null,
  onSave: null,
  options: null,
  className: null,
  baseClassName: "".concat(CLS_PREFIX, "code-editor"),
  scrollPosition: undefined,
  onScroll: undefined
};
export default CodeEditor;